import { Link } from '@tanstack/react-router';
import { ExternalLinkIcon } from 'lucide-react';

import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { usePrChecks } from '@/lib/hooks/usePrChecks';
import { cn } from '@/lib/utils';

export function PrChecksPage() {
  const { data, isLoading } = usePrChecks();

  return (
    <div className="mx-auto max-w-6xl px-8 py-10">
      <header>
        <h1 className="text-3xl font-semibold tracking-tight">PR checks</h1>
        <p className="mt-1 text-sm text-slate-500">
          Every PR Athena saw — with skip reasons, dimensions run, and findings deltas.
        </p>
      </header>

      {!isLoading && data && data.items.length === 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>No PR checks yet</CardTitle>
            <CardDescription>
              <Link to="/app/settings/integrations" className="underline">
                Install the GitHub App
              </Link>{' '}
              and open a PR that touches a watched spec / SDK / docs path.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {(isLoading || (data && data.items.length > 0)) && (
        <div className="mt-6 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900">
              <tr>
                <th className="px-4 py-3 font-medium">Repo / PR</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">New</th>
                <th className="px-4 py-3 font-medium">Resolved</th>
                <th className="px-4 py-3 font-medium">Dimensions</th>
                <th className="px-4 py-3 font-medium">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                    Loading…
                  </td>
                </tr>
              )}
              {data?.items.map((pc) => {
                const s = pc.findings_summary_json ?? {};
                return (
                  <tr key={pc.id}>
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs">
                        {pc.repo_full_name} #{pc.pr_number}
                      </div>
                      <div className="mt-0.5 max-w-xs truncate text-[11px] text-slate-500">
                        {pc.pr_title ?? '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={pc.status} skipped_reason={pc.skipped_reason} />
                    </td>
                    <td className="px-4 py-3">
                      {typeof s.new_findings === 'number' ? s.new_findings : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {typeof s.resolved_findings === 'number' ? s.resolved_findings : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 font-mono text-[10px]">
                        {(pc.dimensions_run ?? []).map((d) => (
                          <span key={d} className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                            {d}
                          </span>
                        ))}
                        {(pc.dimensions_run ?? []).length === 0 && <span className="text-slate-400">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      <div className="flex items-center gap-2">
                        {new Date(pc.created_at).toLocaleString()}
                        {pc.pr_url && (
                          <a
                            href={pc.pr_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-slate-400 hover:text-slate-700"
                            aria-label="Open PR on GitHub"
                          >
                            <ExternalLinkIcon className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({
  status,
  skipped_reason,
}: {
  status: PrCheckOut['status'];
  skipped_reason: string | null;
}) {
  const classes = {
    complete: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
    failed: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
    queued: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
    running: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
    skipped: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
  }[status];
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs capitalize', classes)}>
      {status}
      {status === 'skipped' && skipped_reason && (
        <span className="text-[10px] text-amber-700 dark:text-amber-400">
          · {skipped_reason.replace('_', ' ')}
        </span>
      )}
    </span>
  );
}

type PrCheckOut = NonNullable<ReturnType<typeof usePrChecks>['data']>['items'][number];
